import * as React from "react";
import { useState } from "react";
import DragDropZone from "./DragDropZone";
import QuoteSubmission from "./QuoteSubmission";
import ParentModal from "./SupportedFeaturesModal";
import "bootstrap/dist/css/bootstrap.min.css";
import VisualDisplay from "./VisualDisplay";
import { translate } from "../translator";
import UploadPage from "./UploadPage";
import FeaturesReviewPage from "./FeaturesReview";
import "./Estimator.css";

/**
   Defines the shape of the props that the UploadAndShow component accepts.
*/
interface UploadAndShowProps {
    onFilesSelected?: (files: File[]) => void;
    accessToken: string | null;
}

/**
   Main component that handles drag-and-drop and file submission.
*/
const UploadAndShow: React.FC<UploadAndShowProps> = ({ onFilesSelected, accessToken }) => {
    // State hooks
    const [file, setFile] = useState<File | null>(null); // Only allow one file.
    const [submitted, setSubmitted] = useState(false); // Tracks submission.
    const [jsonResponse, setJsonResponse] = useState<any>(null); // Stores JSON response.
    const [isLoading, setIsLoading] = useState(false); // State for loading.
    const [showModal, setShowModal] = useState(false); // State for modal.
    const [createFromScratch, setCreateFromScratch] = useState(false);

    const allowedFileExtensions = [".dwg", ".dxf"];

    /**
       Event handler for when the user clicks the submit file button.
       Submits the file to the server and captures the JSON response.
    */
    const handleSubmit = async () => {
        if (!file) return;

        setIsLoading(true); // Start loading.

        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASEURL}api/FeatureRecognition/uploadFile`,
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    }
                }
            );

            if (!res.ok) {
                const contentType = res.headers.get("Content-Type") ?? "";

                if (contentType.includes("application/json")) {
                    const errorJson = await res.json();
                    throw new Error(errorJson.error || JSON.stringify(errorJson));
                } else {
                    const errorText = await res.text(); // fallback for plain text
                    throw new Error(`Server error ${res.status}: ${errorText}`);
                }
            }

            const jsonResponse = await res.json(); // Capture JSON responses.
            setJsonResponse(jsonResponse); // Store response in state.
            setSubmitted(true); // Update the state to indicate successful submission.
        } catch (error) {
            if (error instanceof Error) {
                alert("An error occurred while submitting the file. Please try again.\n\n" + error.message);
            }
        } finally {
            setIsLoading(false); // End loading.
        }
    };

    const handleCloseModal = () => setShowModal(false);

    /**
       Event handler for when the user clicks the back button after submission.
    */
    const backToUpload = () => {
        setSubmitted(false);
        setFile(null); // Clear the file after submission.
        setJsonResponse(null); // Clear the JSON response on going back.
    };

    return (
        <div className="upload-and-show">
            {isLoading ? ( // Display loading screen during file upload.
                <div className="loader"></div>
            ) : createFromScratch ? (
                <FeaturesReviewPage
                    jsonResponse={[]} // pass empty features or defaults
                    backToUpload={backToUpload}
                    accessToken={accessToken}
                />
            ) : !submitted ? ( // Display drag-and-drop area if not submitted and not loading.
                <UploadPage
                    file={file}
                    setFile={setFile}
                    onFilesSelected={onFilesSelected}
                    onSubmit={handleSubmit}
                    onCreateFromScratch={() => {
                        setSubmitted(true);
                        setJsonResponse([]); // blank list for new features
                    }}

                />
            ) : (
                <FeaturesReviewPage
                    jsonResponse={jsonResponse}
                    backToUpload={backToUpload}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default UploadAndShow;
